import React from 'react';
import { Truck, Package, MapPin, Clock, AlertCircle } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-20">
      <SEOHead
        title="Shipping & Delivery Policy | Alden Technologies"
        description="Shipping, packaging, transit insurance and delivery timelines for machinery ordered from Alden Technologies."
        path="/shipping-policy"
      />
      <Breadcrumbs items={[{ label: 'Shipping Policy' }]} />
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-12">
          <div className="text-center mb-8 md:mb-12">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Shipping & Delivery Policy</h1>
            <p className="text-gray-600">Last Updated: May 2025</p>
          </div>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <div className="bg-purple-50 border-l-4 border-purple-600 p-4 mb-6">
                <p className="text-gray-800 font-semibold">
                  Alden Technologies provides PAN India delivery and installation services for all industrial machinery and bottling plants.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-purple-600" />
                Delivery Timeline
              </h2>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Complete Bottling Plants:</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>24-30 BPM Plants: 45-60 days from order confirmation</li>
                    <li>40-60 BPM Plants: 60-75 days from order confirmation</li>
                    <li>80-120 BPM Plants: 75-90 days from order confirmation</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Individual Machinery:</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Standard Models: 15-30 days</li>
                    <li>Customized Equipment: 30-45 days</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Spare Parts:</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>In-stock items: 3-5 working days</li>
                    <li>Made-to-order parts: 10-15 working days</li>
                  </ul>
                </div>
              </div>

              <p className="text-gray-700 mt-4 italic">
                <AlertCircle className="inline w-4 h-4 text-orange-500" /> Timelines are approximate and may vary based on customization requirements, material availability, and site readiness.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-purple-600" />
                Service Coverage
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>PAN India Delivery:</strong> We deliver to all states and union territories in India</li>
                <li><strong>Installation Services:</strong> Complete turnkey installation available across India</li>
                <li><strong>Remote Locations:</strong> Accessible locations; additional charges may apply for extremely remote areas</li>
                <li><strong>International Delivery:</strong> Available on request (custom quotation required)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-6 h-6 text-purple-600" />
                Shipping Process
              </h2>
              
              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Step 1: Manufacturing</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Customer receives regular updates on manufacturing progress</li>
                <li>Quality checks performed at each stage</li>
                <li>Pre-dispatch testing and trial run</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Step 2: Packaging</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Industrial-grade packaging for transit safety</li>
                <li>Wooden crates for heavy machinery</li>
                <li>Weather-resistant covering</li>
                <li>Proper labeling with handling instructions</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Step 3: Transportation</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Reliable transport partners for safe delivery</li>
                <li>Transit insurance available (recommended for high-value orders)</li>
                <li>Real-time tracking information provided</li>
                <li>Expected delivery date communicated 3-5 days in advance</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Step 4: Delivery & Installation</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Equipment delivered to customer site</li>
                <li>Installation team arrives as per schedule</li>
                <li>Complete installation, testing, and commissioning</li>
                <li>Staff training on equipment operation and maintenance</li>
                <li>Handover with documentation and warranty card</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Shipping Charges</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Complete Plants:</strong> Free delivery and installation for orders above ₹15 lakhs (within 500 km radius of Jaipur)</li>
                <li><strong>Beyond 500 km:</strong> Actual transportation charges applicable</li>
                <li><strong>Individual Machinery:</strong> Shipping charges vary based on weight, dimensions, and destination</li>
                <li><strong>Spare Parts:</strong> Courier charges extra (typically ₹200-500 depending on weight)</li>
                <li><strong>Express Delivery:</strong> Available on request at additional cost</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Customer Responsibilities</h2>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">Before Delivery:</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Ensure site is ready as per specifications provided</li>
                  <li>Arrange for adequate space for unloading and installation</li>
                  <li>Provide three-phase electricity connection</li>
                  <li>Arrange water source for RO plants</li>
                  <li>Obtain necessary permissions from building management (if applicable)</li>
                </ul>

                <h3 className="font-bold text-gray-900 mb-3">During Delivery:</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Be present or arrange for authorized representative</li>
                  <li>Inspect packaging for external damage</li>
                  <li>Sign delivery receipt after visual inspection</li>
                  <li>Report any transit damage immediately (within 24 hours)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Delay Scenarios</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Manufacturing Delays:</strong> Customer notified immediately; revised delivery date provided</li>
                <li><strong>Site Not Ready:</strong> Equipment can be stored at our facility (storage charges: ₹5,000/month)</li>
                <li><strong>Customer Unavailability:</strong> Rescheduling charges may apply for second delivery attempt</li>
                <li><strong>Force Majeure:</strong> Natural calamities, strikes, or government restrictions may cause delays</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Transit Damage</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Report damage within 24 hours of delivery with photographs</li>
                <li>Do not accept delivery if packaging is severely damaged</li>
                <li>We will arrange replacement/repair at no additional cost</li>
                <li>Transit insurance claims processed within 15-20 days</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Tracking & Updates</h2>
              <p className="text-gray-700 mb-4">
                Customers receive regular updates via:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Email notifications at each stage</li>
                <li>WhatsApp updates (if opted in)</li>
                <li>Phone calls for delivery scheduling</li>
                <li>Order tracking through customer support</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Contact for Delivery Queries</h2>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Alden Technologies</p>
                <p className="text-gray-700">KH No 588, Machara Harmada, Jaipur, Rajasthan - 302013</p>
                <p className="text-gray-700">Email: aldentechnologiesindia@gmail.com</p>
                <p className="text-gray-700">Phone: +91-9351292512</p>
                <p className="text-gray-700">GST: 08BUJPR0377M1ZY</p>
                <p className="text-gray-700">Working Hours: Monday to Saturday, 9:00 AM - 6:00 PM</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;